/**
 * Quick Filter Configurations
 *
 * Per-entity configs that define which fields appear in the
 * collapsible QuickFilterPanel as inline controls.
 *
 * Each config maps a schema field to a specific control type,
 * operator, and display properties.
 */

import type { FilterOperator, SavedViewEntityType } from '../api/types'

/* ── Config Type ──────────────────────────────────────────────── */

export interface QuickFilterConfig {
    /** Schema field name (must match schema field.name) */
    field: string
    /** Display label for the inline control */
    label: string
    /** Control type to render */
    control: 'search' | 'select' | 'multi-select' | 'number' | 'date' | 'boolean'
    /** Default operator applied when the value is set */
    operator: FilterOperator
    /** Placeholder text for inputs */
    placeholder?: string
    /** Step value for number inputs */
    step?: number
    /** Min value for number inputs */
    min?: number
    /** Static options for select / multi-select controls */
    options?: { value: string; label: string }[]
    /** Async loader for dynamic options (lazy-loaded on first render) */
    asyncOptions?: () => Promise<{ value: string; label: string }[]>
    /** Width hint: narrow (90px), normal (default), wide (flex:1) */
    width?: 'narrow' | 'normal' | 'wide'
    /** Which row to render in (1 or 2, defaults to 1) */
    row?: 1 | 2
}

/* ── Weather Event Config ─────────────────────────────────────── */

const weatherEventConfigs: QuickFilterConfig[] = [
    // Row 1
    {
        field: 'search',
        label: 'Search',
        control: 'search',
        operator: '$contains',
        placeholder: 'Search properties...',
        width: 'wide',
        row: 1,
    },
    {
        field: 'eventType',
        label: 'Event Type',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Types',
        options: [
            { value: 'hail', label: 'Hail' },
            { value: 'tornado', label: 'Tornado' },
            { value: 'wind', label: 'Wind' },
        ],
        row: 1,
    },

    {
        field: 'confidence',
        label: 'Confidence',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Levels',
        options: [
            { value: 'radar_detected', label: 'Radar Detected' },
            { value: 'human_verified', label: 'Human Verified' },
            { value: 'radar_and_human_verified', label: 'Confirmed' },
        ],
        row: 1,
    },

    // Row 2
    {
        field: 'eventDate',
        label: 'From',
        control: 'date',
        operator: '$gte',
        row: 2,
    },
    {
        field: 'eventDate',
        label: 'To',
        control: 'date',
        operator: '$lte',
        row: 2,
    },
    {
        field: 'hailSizeIN',
        label: 'Hail Size (in)',
        control: 'number',
        operator: '$gte',
        placeholder: '—',
        step: 0.25,
        min: 0,
        width: 'narrow',
        row: 2,
    },
    {
        field: 'distanceMiles',
        label: 'Distance (mi)',
        control: 'number',
        operator: '$lte',
        placeholder: '—',
        step: 1,
        min: 0,
        width: 'narrow',
        row: 2,
    },
    {
        field: 'hasContactWithEmail',
        label: 'Has Email',
        control: 'boolean',
        operator: '$eq',
        row: 2,
    },
    {
        field: 'hasContactWithPhone',
        label: 'Has Phone',
        control: 'boolean',
        operator: '$eq',
        row: 2,
    },
]

/* ── IT Request Config ────────────────────────────────────────── */

const itRequestConfigs: QuickFilterConfig[] = [
    {
        field: 'search',
        label: 'Search',
        control: 'search',
        operator: '$contains',
        placeholder: 'Search requests...',
        width: 'wide',
        row: 1,
    },
    {
        field: 'status',
        label: 'Status',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Statuses',
        options: [
            { value: 'new', label: 'New' },
            { value: 'open', label: 'Open' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'pending', label: 'Pending' },
            { value: 'delegated', label: 'Delegated' },
            { value: 'on_hold', label: 'On Hold' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'closed', label: 'Closed' },
            { value: 'cancelled', label: 'Cancelled' },
        ],
        row: 1,
    },
    {
        field: 'priority',
        label: 'Priority',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Priorities',
        options: [
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
            { value: 'critical', label: 'Critical' },
        ],
        row: 1,
    },
    {
        field: 'category',
        label: 'Category',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Categories',
        asyncOptions: async () => {
            const { itRequestService } = await import('@/modules/tickets/api/service')
            const cats = await itRequestService.listCategories()
            return cats
                .filter(c => c.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(c => ({ value: c.slug, label: c.name }))
        },
        row: 1,
    },
    {
        field: 'effort',
        label: 'Effort',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Efforts',
        options: [
            { value: 'extra_small', label: 'Extra Small' },
            { value: 'small', label: 'Small' },
            { value: 'medium', label: 'Medium' },
            { value: 'large', label: 'Large' },
            { value: 'extra_large', label: 'Extra Large' },
        ],
        row: 1,
    },
]

/* ── Contract Review Config ───────────────────────────────────── */

const contractReviewConfigs: QuickFilterConfig[] = [
    {
        field: 'search',
        label: 'Search',
        control: 'search',
        operator: '$contains',
        placeholder: 'Search contracts...',
        width: 'wide',
        row: 1,
    },
    {
        field: 'standardFormIdentified',
        label: 'Standard Form',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Forms',
        options: [
            { value: 'AIA A401-2017', label: 'AIA A401-2017' },
            { value: 'AIA A201-2017', label: 'AIA A201-2017' },
            { value: 'ConsensusDocs 750', label: 'ConsensusDocs 750' },
        ],
        row: 1,
    },
]

/* ── Fab Job Config ───────────────────────────────────────────── */

const fabJobConfigs: QuickFilterConfig[] = [
    {
        field: 'search',
        label: 'Search',
        control: 'search',
        operator: '$contains',
        placeholder: 'Search jobs...',
        width: 'wide',
        row: 1,
    },
    {
        field: 'location',
        label: 'Location',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Locations',
        options: [
            { value: 'omaha', label: 'Omaha' },
            { value: 'kc', label: 'Kansas City' },
        ],
        row: 1,
    },
    {
        field: 'status',
        label: 'Status',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Statuses',
        options: [
            { value: 'not_started', label: 'Not Started' },
            { value: 'in_fabrication', label: 'In Fabrication' },
            { value: 'ready_to_ship', label: 'Ready to Ship' },
            { value: 'shipped', label: 'Shipped' },
            { value: 'on_hold', label: 'On Hold' },
            { value: 'service', label: 'Service' },
            { value: 'canceled', label: 'Canceled' },
        ],
        row: 1,
    },
    {
        field: 'projectManagerUser',
        label: 'PM',
        control: 'select',
        operator: '$in',
        placeholder: 'All PMs',
        asyncOptions: async () => {
            const { fabSchedulerService } = await import('@/modules/fab-schedule/api/service')
            const pms = await fabSchedulerService.getProjectManagers()
            return pms.map(p => ({ value: p.id, label: p.label }))
        },
        row: 1,
    },
]

/* ── Material Preset Config ───────────────────────────────────── */

const optimizerMaterialPresetConfigs: QuickFilterConfig[] = [
    {
        field: 'search',
        label: 'Search',
        control: 'search',
        operator: '$contains',
        placeholder: 'Search presets by name...',
        width: 'wide',
        row: 1,
    },
    {
        field: 'materialType',
        label: 'Material Type',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Material Types',
        asyncOptions: async () => {
            const { savedViewService } = await import('../api/service')
            const vals = await savedViewService.getFieldValues('optimizer_material_preset', 'materialType')
            return (vals as string[]).filter(Boolean).map(v => ({ value: v, label: v }))
        },
        row: 1,
    },
    {
        field: 'vendor',
        label: 'Vendor',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Vendors',
        asyncOptions: async () => {
            const { savedViewService } = await import('../api/service')
            const vals = await savedViewService.getFieldValues('optimizer_material_preset', 'vendor')
            return (vals as string[]).filter(Boolean).map(v => ({ value: v, label: v }))
        },
        row: 1,
    },
    {
        field: 'finish',
        label: 'Finish',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Finishes',
        asyncOptions: async () => {
            const { savedViewService } = await import('../api/service')
            const vals = await savedViewService.getFieldValues('optimizer_material_preset', 'finish')
            return (vals as string[]).filter(Boolean).map(v => ({ value: v, label: v }))
        },
        row: 1,
    },
    {
        field: 'thickness',
        label: 'Thickness',
        control: 'select',
        operator: '$eq',
        placeholder: 'All Thicknesses',
        asyncOptions: async () => {
            const { savedViewService } = await import('../api/service')
            const vals = await savedViewService.getFieldValues('optimizer_material_preset', 'thickness')
            return (vals as string[]).filter(Boolean).map(v => ({ value: v, label: v }))
        },
        row: 1,
    },
    {
        field: 'isDirectional',
        label: 'Directional',
        control: 'boolean',
        operator: '$eq',
        row: 1,
    },
]

/* ── Registry ─────────────────────────────────────────────────── */

const configRegistry: Partial<Record<SavedViewEntityType, QuickFilterConfig[]>> = {
    weather_event: weatherEventConfigs,
    it_request: itRequestConfigs,
    contract_review: contractReviewConfigs,
    fab_job: fabJobConfigs,
    optimizer_material_preset: optimizerMaterialPresetConfigs,
}

/**
 * Get quick filter configs for an entity type.
 * Returns an empty array if no quick filters are configured.
 */
export function getQuickFilterConfigs(entityType: SavedViewEntityType): QuickFilterConfig[] {
    return configRegistry[entityType] ?? []
}
